'use client'

import Link from 'next/link'
import { useStorefrontErrorReporting } from '@ventanas95dev/runtime/useStorefrontErrorReporting'
import { createContext, type ReactNode, useContext } from 'react'

export type SpecialPageContent = {
  error: {
    description: string
    retry: string
    title: string
  }
  homeHref: string
  loading: {
    label: string
  }
  notFound: {
    backHome: string
    description: string
    title: string
  }
}

export type StorefrontErrorBoundaryProps = {
  error: Error & { digest?: string }
  preview?: boolean
  retry: () => void
}

const SpecialPageContentContext = createContext<SpecialPageContent | null>(null)

export function SpecialPageContentProvider({
  children,
  content,
}: {
  children: ReactNode
  content: SpecialPageContent
}) {
  return (
    <SpecialPageContentContext.Provider value={content}>
      {children}
    </SpecialPageContentContext.Provider>
  )
}

export function LocalizedNotFoundPage() {
  const { homeHref, notFound } = useSpecialPageContent()

  return (
    <main className="flex min-h-svh items-center justify-center bg-white px-6 py-16 text-black">
      <section className="w-full max-w-2xl border-2 border-black p-8 sm:p-12">
        <p className="text-sm font-semibold tracking-[0.2em] uppercase">404</p>
        <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-6xl">{notFound.title}</h1>
        <p className="mt-5 max-w-xl text-lg leading-7">{notFound.description}</p>
        <Link
          className="mt-8 inline-flex border-2 border-black bg-black px-5 py-3 font-semibold text-white transition hover:bg-white hover:text-black focus:outline-none focus:ring-4 focus:ring-black/20"
          href={homeHref}
        >
          {notFound.backHome}
        </Link>
      </section>
    </main>
  )
}

export function LocalizedErrorPage({
  error,
  preview = false,
  retry,
}: StorefrontErrorBoundaryProps) {
  const { error: copy } = useSpecialPageContent()

  useStorefrontErrorReporting(error, { preview, boundary: 'localized-error' })

  return (
    <main className="flex min-h-svh items-center justify-center bg-white px-6 py-16 text-black">
      <section className="w-full max-w-2xl border-2 border-black p-8 sm:p-12">
        <h1 className="text-4xl font-bold tracking-tight sm:text-6xl">{copy.title}</h1>
        <p className="mt-5 max-w-xl text-lg leading-7">{copy.description}</p>
        <button
          className="mt-8 inline-flex border-2 border-black bg-black px-5 py-3 font-semibold text-white transition hover:bg-white hover:text-black focus:outline-none focus:ring-4 focus:ring-black/20"
          onClick={() => retry()}
          type="button"
        >
          {copy.retry}
        </button>
      </section>
    </main>
  )
}

function useSpecialPageContent(): SpecialPageContent {
  const content = useContext(SpecialPageContentContext)
  if (!content) {
    throw new Error('Special-page components must render inside the locale layout.')
  }
  return content
}
